/**
 * t246u: what an issue card's activity line says happened: "set severity to High", "assigned Sam Reid", "moved it to
 * In progress", "commented: On my way". Never a bare "Updated". Built from the issue's history (incident events), which
 * records every field change with its old and new value; `name` is the readable new value (assignee, team, category).
 */
import { INCIDENT_SEVERITIES, INCIDENT_STATUSES, assigneeIdsOf, incidentEventText, listNames } from '../ops/incidents';
import { feedNewestComment, type FeedItem } from './feed';

export interface FeedIssueChange { field: string; from?: string | null; to?: string | null; name?: string | null;
  /** t699u: names by user id, for assignee changes (from / to are comma-separated ids). */
  names?: Record<string, string> | null }
type IssueItem = Extract<FeedItem, { kind: 'issue' }>;
export interface FeedPerson { id: string; name: string }
/** The line as pieces: a person (opens their profile) and plain text, e.g. [person, ' set severity to High']. */
export type ActivitySeg = string | FeedPerson;

const CLOSED = new Set(['resolved', 'closed']);
const FIELD_RANK = ['status', 'assignee', 'severity', 'team', 'category', 'riddor_flag', 'edited'];
const sev = (v?: string | null) => INCIDENT_SEVERITIES.find(s => s.key === v)?.label ?? v ?? '';
const st = (v?: string | null) => INCIDENT_STATUSES.find(s => s.key === v)?.label ?? v ?? '';

/** t699u: "took it on", "assigned Sam Reid and Jo Bell", "removed Sam Reid", "unassigned it". */
function assigneeChangeText(c: FeedIssueChange, actorId?: string | null): string {
  const before = assigneeIdsOf(c.from); const after = assigneeIdsOf(c.to);
  if (!after.length) return 'unassigned it';
  const name = (id: string) => c.names?.[id] ?? (after.length === 1 ? c.name : null) ?? 'someone';
  const added = after.filter(id => !before.includes(id));
  if (added.length === 1 && added[0] === actorId) return 'took it on';
  if (added.length) return `assigned ${listNames(added.map(id => (id === actorId ? 'themselves' : name(id))))}`;
  const removed = before.filter(id => !after.includes(id));
  return removed.length ? `removed ${listNames(removed.map(id => (id === actorId ? 'themselves' : c.names?.[id] ?? 'someone')))}` : `assigned ${listNames(after.map(name))}`;
}

/** One change as the words after the person's name. */
export function issueChangeText(c: FeedIssueChange, actorId?: string | null): string {
  switch (c.field) {
    case 'status':
      if (c.from && CLOSED.has(c.from) && c.to && !CLOSED.has(c.to)) return 'reopened it';
      if (c.to === 'resolved') return 'resolved it';
      if (c.to === 'closed') return 'closed it';
      if (c.to === 'acknowledged') return 'acknowledged it';
      if (c.to === 'on_hold') return 'put it on hold';
      return `moved it to ${st(c.to)}`;
    case 'severity': return `set severity to ${sev(c.to)}`;
    case 'assignee': return assigneeChangeText(c, actorId);
    case 'team': return c.to ? `assigned the ${c.name ?? 'other'} team` : 'removed the team';
    case 'category': return c.to ? `set category to ${c.name ?? 'another'}` : 'removed the category';
    case 'riddor_flag': return c.to === 'true' ? 'flagged possible RIDDOR' : 'cleared the RIDDOR flag';
    case 'edited': return incidentEventText('edited', null, c.to ?? null); // older rows don't say which fields
    case 'photo': return 'added a photo';
    case 'due_date': return !c.to ? 'removed the due date' : !c.from ? 'set the due date' : 'changed the due date'; // t565u, t706u
    default: return c.field.replace(/_/g, ' ');
  }
}

/** Several changes saved together: the most meaningful, or the top two joined ("moved it to In progress and assigned Sam Reid"). */
export function issueChangesText(changes: FeedIssueChange[] | undefined, actorId?: string | null): string {
  const rank = (f: string) => { const i = FIELD_RANK.indexOf(f); return i < 0 ? FIELD_RANK.length : i; };
  const top = [...(changes ?? [])].sort((a, b) => rank(a.field) - rank(b.field)).slice(0, 2).map(c => issueChangeText(c, actorId));
  return [...new Set(top)].join(' and ') || 'edited the details';
}

const snippet = (s: string, n = 60) => { const x = s.replace(/\s+/g, ' ').trim(); return x.length > n ? `${x.slice(0, n - 1).trimEnd()}…` : x; };

/**
 * The issue card's activity: who did the latest thing and what. `withComment`: when a comment is newer than the issue's
 * own latest event and the card doesn't already show it, the comment is the activity ("commented: Parts ordered").
 */
export function issueActivity(i: IssueItem, o: { withComment?: boolean } = {}): ActivitySeg[] {
  const c = feedNewestComment(i);
  const ownAt = i.event_at ?? i.at;
  if (o.withComment && c && c.at > ownAt) {
    const who: ActivitySeg = c.mine ? 'You' : c.actor_id ? { id: c.actor_id, name: c.actor_name } : c.actor_name;
    const body = snippet(c.body);
    return [who, body ? ` ${c.reply_to ? 'replied' : 'commented'}: ${body}` : ' added a photo'];
  }
  if (i.event === 'reported') return i.actor ? ['Reported by ', i.actor] : ['Reported'];
  if (i.event === 'resolved') return i.actor ? ['Resolved by ', i.actor] : ['Resolved'];
  const text = issueChangesText(i.changes, i.actor?.id);
  if (!i.actor) return [text.charAt(0).toUpperCase() + text.slice(1)];
  return [i.actor, ` ${text}`];
}

export const activityText = (segs: ActivitySeg[]) => segs.map(s => (typeof s === 'string' ? s : s.name)).join('');
